import React, { useState, useRef } from "react";
import "./KgSidebar.css";


interface DocumentLink {
  name: string;
  url: string;
  version?: string;
  updated?: string;
  owner?: string;
  project_id?: string;
  file_type?: string;
  page_number?: string | number | (string | number)[];
  link_strengths?: {
    type: string;
    value: number;
    confidence_score?: number;
  }[];
}

interface KgSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  documents: DocumentLink[];
  onAddToChat?: (docName: string) => void;
}

const formatPageNumber = (page: any) => {
  if (!page && page !== 0) return "";
  if (Array.isArray(page)) {
    return page.join(", ");
  }
  let str = String(page).trim();
  str = str.replace(/^pages?\s*:?\s*/i, "").trim();
  return str;
};

const KgSidebar: React.FC<KgSidebarProps> = ({ isOpen, onClose, documents, onAddToChat }) => {
  const [width, setWidth] = useState(530);
  const isResizing = useRef(false);

  const startResizing = () => {
    isResizing.current = true;
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", stopResizing);
    document.body.style.cursor = "col-resize";
  };

  const stopResizing = () => {
    isResizing.current = false;
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", stopResizing);
    document.body.style.cursor = "default";
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isResizing.current) return;
    const newWidth = window.innerWidth - e.clientX;
    const minWidth = 300;
    const maxWidth = 600;
    if (newWidth >= minWidth && newWidth <= maxWidth) {
      setWidth(newWidth);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="kg-sidebar" style={{ width: `${width}px` }}>
      <div className="resize-handle" onMouseDown={startResizing}>
        <div className="resize-pill" />
      </div>

      {/* Toggle Handle at the very top left edge */}
      <div className="sidebar-toggle-handle" onClick={onClose}>
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </div>

      <div className="kg-sidebar-header">
        <h3 className="header-title">Source document</h3>
        <div className="doc-count-main">{documents.length} {documents.length === 1 ? 'file' : 'files'}</div>
      </div>

      <div className="kg-sidebar-content">
        {documents.length > 0 ? (
          documents.map((doc, index) => {
            const extension = doc.name.split(".").pop();
            const fileNameWithoutExtension = doc.name.replace(`.${extension}`, "");

            return (
              <div className="kg-doc-item" key={index}>
                <div className="kg-doc-info">
                  <div className="kg-doc-title">
                    <span className="filename">{fileNameWithoutExtension}
                      <span className="filetype">{doc.file_type || extension}</span>
                    </span>
                  </div>

                  <div className="kg-doc-meta">
                    {Boolean(doc.page_number && formatPageNumber(doc.page_number)) && (
                      <div>Page : {formatPageNumber(doc.page_number)}</div>
                    )}
                    <div>Dossier ID: {doc.project_id}</div>
                    <div>Last Modified: {doc.updated}</div>
                  </div>

                  {doc.link_strengths && doc.link_strengths.length > 0 && (
                    <div className="kg-doc-tags">
                      {doc.link_strengths.map((strength: any, sIndex: number) => (
                        <span key={sIndex} className={`kg-tag kg-tag-${strength.type.toLowerCase()}`}>
                          {strength.type} <span className="kg-tag-value">
                            {(() => {
                              const val = strength.confidence_score || strength.value || 0;
                              return val <= 1 && val > 0 ? Math.round(val * 100) : Math.round(val);
                            })()}%
                          </span>
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="kg-doc-actions">
                    <button
                      className="action-icon-btn"
                      title="Zoom In"
                      onClick={() => onAddToChat?.(doc.name)}
                    >
                      <img src="svg/chatG.svg" width={16} height={16} alt="Zoom In" />
                    </button>
                    <button
                      className="action-icon-btn"
                      title="Expand"
                      onClick={() => window.open(doc.url, '_blank')}
                      style={{ display: "none" }}
                    >
                      <img src="svg/expand.svg" width={16} height={16} alt="Expand" />
                    </button>
                    <button
                      className="action-icon-btn"
                      title="View Document"
                      onClick={(e) => {
                        e.stopPropagation();
                        const url = doc.url && doc.url !== "#" ? doc.url : `${window.location.origin}/document-store/get_file_bytes?file_name=${encodeURIComponent(doc.name)}`;
                        window.open(url, '_blank');
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <img src="svg/expand.svg" width={16} height={16} alt="View" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="kg-not-found">Not Found</div>
        )}
      </div>
    </div>
  );
};

export default KgSidebar;
